import React, { useState, useCallback, useRef, memo } from 'react';
import {
  Card,
  Input,
  Select,
  Typography,
  Divider,
  message,
} from 'antd';
import {
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { WorkOrder } from '../../types';

const { Text } = Typography;

export interface WorkOrderFormFieldsProps {
  workOrder: WorkOrder;
  isMobile: boolean;
  isLocked: boolean;
  onFieldSave: (field: string, value: any) => Promise<void>;
  setIsEditing: (editing: boolean) => void;
}

interface InlineEditFieldProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  label: string;
  isLocked: boolean;
  multiline?: boolean;
  setIsEditing: (editing: boolean) => void;
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  label,
  isLocked,
  multiline = false,
  setIsEditing,
}) => {
  const [editing, setEditingLocal] = useState(false);
  const [draft, setDraft] = useState(value);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startEdit = useCallback(() => {
    if (isLocked) return;
    setDraft(value);
    setEditingLocal(true);
    setIsEditing(true);
  }, [isLocked, value, setIsEditing]);

  const handleSave = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        if (draft !== value) {
          await onSave(draft);
        }
      } catch {
        message.error('Could not save changes. Check your connection and try again.');
      } finally {
        setEditingLocal(false);
        setIsEditing(false);
      }
    }, 500);
  }, [draft, value, onSave, setIsEditing]);

  if (!editing) {
    return (
      <div
        onClick={startEdit}
        style={{
          cursor: isLocked ? 'default' : 'pointer',
          minHeight: 48,
          padding: '8px 0',
        }}
      >
        <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        <div style={{ fontSize: 14, fontWeight: 500, color: value ? '#111827' : '#9ca3af', marginTop: 2 }}>
          {value || 'Tap to edit'}
        </div>
      </div>
    );
  }

  if (multiline) {
    return (
      <div style={{ padding: '8px 0' }}>
        <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
        <Input.TextArea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleSave}
          autoFocus
          autoSize={{ minRows: 2, maxRows: 6 }}
          style={{ fontSize: 16, marginTop: 4 }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
      <Input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        autoFocus
        style={{ fontSize: 16, marginTop: 4 }}
      />
    </div>
  );
};

const STATUS_OPTIONS: { value: WorkOrder['status']; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_parts', label: 'Waiting Parts' },
  { value: 'waiting_approval', label: 'Waiting Approval' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WorkOrderFormFields: React.FC<WorkOrderFormFieldsProps> = ({
  workOrder,
  isMobile,
  isLocked,
  onFieldSave,
  setIsEditing,
}) => {
  const [editingStatus, setEditingStatus] = useState(false);

  const handleStatusChange = useCallback(async (newStatus: WorkOrder['status']) => {
    try {
      await onFieldSave('status', newStatus);
      message.success('Status updated');
    } catch {
      message.error('Failed to update status');
    } finally {
      setEditingStatus(false);
      setIsEditing(false);
    }
  }, [onFieldSave, setIsEditing]);

  const handleFieldSave = useCallback((field: string) => async (value: string) => {
    await onFieldSave(field, value);
  }, [onFieldSave]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>
      {/* Status Edit */}
      <Card className="shadow-sm" styles={{ body: { padding: isMobile ? '14px' : '16px' } }} style={{ overflow: 'hidden' }}>
        <div style={{ minHeight: 48 }}>
          <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>
            Status
          </Text>
          {editingStatus && !isLocked ? (
            <Select
              value={workOrder.status}
              onChange={handleStatusChange}
              onBlur={() => { setEditingStatus(false); setIsEditing(false); }}
              options={STATUS_OPTIONS}
              autoFocus
              open
              style={{ width: '100%', fontSize: 16 }}
              size="large"
            />
          ) : (
            <div
              onClick={() => {
                if (!isLocked) {
                  setEditingStatus(true);
                  setIsEditing(true);
                }
              }}
              style={{
                cursor: isLocked ? 'default' : 'pointer',
                fontSize: 14,
                fontWeight: 500,
                padding: '4px 0',
              }}
            >
              {workOrder.status.replace(/_/g, ' ').toUpperCase()}
            </div>
          )}
        </div>
      </Card>

      {/* Vehicle & Customer Card */}
      <Card className="shadow-sm" styles={{ body: { padding: isMobile ? '14px' : '16px' } }} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 20 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CarOutlined style={{ fontSize: 16, color: '#3b82f6' }} />
              </div>
              <Text strong style={{ fontSize: 13 }}>Vehicle</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', wordWrap: 'break-word', minHeight: 48, padding: '8px 0' }}>
                {workOrder.vehicle?.year} {workOrder.vehicle?.make} {workOrder.vehicle?.model}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', wordWrap: 'break-word' }}>VIN: {workOrder.vehicle?.vin || 'N/A'}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Mileage: {workOrder.vehicle?.current_mileage?.toLocaleString() || 'N/A'}
              </div>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <UserOutlined style={{ fontSize: 16, color: '#22c55e' }} />
              </div>
              <Text strong style={{ fontSize: 13 }}>Customer</Text>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', wordWrap: 'break-word', minHeight: 48, padding: '8px 0' }}>
                {workOrder.customer?.company_name ||
                  `${workOrder.customer?.first_name} ${workOrder.customer?.last_name}`}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4, wordWrap: 'break-word' }}>
                <PhoneOutlined /> {workOrder.customer?.phone}
              </div>
              {workOrder.customer?.email && (
                <div style={{ fontSize: 12, color: '#6b7280', wordWrap: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}>{workOrder.customer.email}</div>
              )}
            </div>
          </div>
        </div>

        {/* Problem Description - inline editable */}
        <Divider style={{ margin: isMobile ? '14px 0' : '16px 0' }} />
        <InlineEditField
          value={workOrder.problem_description || ''}
          onSave={handleFieldSave('problem_description')}
          label="Customer Complaint"
          isLocked={isLocked}
          multiline
          setIsEditing={setIsEditing}
        />
      </Card>

      {/* Assignment Card - inline editable */}
      <Card
        title={<span style={{ fontSize: 13 }}>Assignment</span>}
        className="shadow-sm"
        styles={{ body: { padding: isMobile ? '14px' : '16px' } }}
        style={{ overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <InlineEditField
            value={workOrder.assigned_advisor || ''}
            onSave={handleFieldSave('assigned_advisor')}
            label="Advisor"
            isLocked={isLocked}
            setIsEditing={setIsEditing}
          />
          <div style={{ minHeight: 48, padding: '8px 0' }}>
            <Text style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', display: 'block', marginBottom: 4 }}>
              Technician
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ToolOutlined style={{ color: '#3b82f6' }} />
              <InlineEditField
                value={workOrder.assigned_technician || ''}
                onSave={handleFieldSave('assigned_technician')}
                label=""
                isLocked={isLocked}
                setIsEditing={setIsEditing}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default memo(WorkOrderFormFields);
